import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import StrategicPointEditor from '@/components/strategic-points/StrategicPointEditor'
import StrategicPointsSidebar from '@/components/strategic-points/StrategicPointsSidebar'
import { useMapLabel } from '@/components/strategy/MapSelector'
import { editorIconButton } from '@/components/strategy/editorUi'
import { sectionPath } from '@/lib/strategyKinds'
import { useLanguage } from '@/contexts/LanguageContext'
import { fetchCallouts, type Callout } from '@/lib/callouts'

export type SPTool = 'select' | 'point' | 'ping' | 'angle_polygon' | 'angle_arrow' | 'callout'

export interface StrategicPointData {
  id: number
  map_name: string
  x: number
  y: number
  label: string
  description: string
  color: string
  icon_type: string
  viewing_angles: ViewingAngle[]
  created_by: string
  created_by_name: string
  created_by_avatar: string
  created_at: number
  updated_at: number
  media: MediaItem[]
}

export interface ViewingAngle {
  type: 'polygon' | 'arrow'
  points: { x: number; y: number }[]
  color: string
  fill_opacity?: number
}

export interface MediaItem {
  id: number
  point_id: number
  filename: string
  media_type: 'image' | 'video'
  url: string
  file_size: number
  created_at: number
}

export default function StrategicPointsMap() {
  const { mapName } = useParams<{ mapName: string }>()
  const navigate = useNavigate()
  const { T } = useLanguage()

  const [points, setPoints] = useState<StrategicPointData[]>([])
  const [selectedPointId, setSelectedPointId] = useState<number | null>(null)
  // Dar ekranda varsayılan kapalı: w-72 kenar çubuğu açıkken telefonda tuvale
  // yer kalmaz. md altında kenar çubuğu tuvalin üstüne bir katman olarak açılır.
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 768)
  const [tool, setTool] = useState<SPTool>('select')
  const [loading, setLoading] = useState(true)
  // Mevkiler aynı haritanın ikinci katmanı. Seçim noktalarınkiyle birbirini
  // dışlar: sağ panel aynı anda tek bir şeyi anlatır.
  const [callouts, setCallouts] = useState<Callout[]>([])
  const [selectedCalloutId, setSelectedCalloutId] = useState<number | null>(null)

  const label = useMapLabel(mapName ?? '') || T.strategy.map

  // Fetch points
  const fetchPoints = useCallback(async () => {
    if (!mapName) return
    setLoading(true)
    try {
      const res = await fetch(`/api/strategic-points?map_name=${mapName}`, { credentials: 'include' })
      if (res.ok) setPoints(await res.json())
    } catch { /* ignore */ }
    setLoading(false)
  }, [mapName])

  useEffect(() => { fetchPoints() }, [fetchPoints])

  useEffect(() => {
    if (!mapName) return
    let cancelled = false
    fetchCallouts(mapName).then(rows => { if (!cancelled) setCallouts(rows) }).catch(() => {})
    return () => { cancelled = true }
  }, [mapName])

  const handleSelectCallout = useCallback((id: number | null) => {
    setSelectedCalloutId(id)
    if (id !== null) setSelectedPointId(null)
  }, [])

  const handleAddCallout = useCallback((c: Callout) => {
    setCallouts(prev => [...prev, c])
  }, [])

  const handleUpdateCallout = useCallback((c: Callout) => {
    setCallouts(prev => prev.map(x => x.id === c.id ? c : x))
  }, [])

  /** Yetki backend'de (`callouts/router.py` → `_can_write`). */
  const deleteCallout = useCallback(async (id: number) => {
    try {
      const res = await fetch(`/api/callouts/${id}`, { method: 'DELETE', credentials: 'include' })
      if (!res.ok) return
      setCallouts(prev => prev.filter(c => c.id !== id))
      setSelectedCalloutId(prev => prev === id ? null : prev)
    } catch { /* ignore */ }
  }, [])

  // CRUD handlers
  const handleAddPoint = useCallback((point: StrategicPointData) => {
    setPoints(prev => [...prev, point])
  }, [])

  const handleUpdatePoint = useCallback((id: number, updates: Partial<StrategicPointData>) => {
    setPoints(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p))
  }, [])

  const handleSelectPoint = useCallback((id: number | null) => {
    setSelectedPointId(id)
    if (id !== null) setSelectedCalloutId(null)
  }, [])

  const handleRemovePoint = useCallback((id: number) => {
    setPoints(prev => prev.filter(p => p.id !== id))
    setSelectedPointId(prev => prev === id ? null : prev)
  }, [])

  /**
   * Silme uçtan geçer: yetki backend'de (`_check_point_access`) — sahibi ve
   * yönetim siler. Kenar çubuğu düğmeyi zaten gizliyor, ama gerçek kural orada.
   */
  const deletePoint = useCallback(async (id: number) => {
    try {
      const res = await fetch(`/api/strategic-points/${id}`, {
        method: 'DELETE', credentials: 'include',
      })
      if (res.ok) handleRemovePoint(id)
    } catch { /* ignore */ }
  }, [handleRemovePoint])

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Topbar ────────────────────────────────────────────────── */}
      {/* Strateji editörüyle **aynı hat**: 68px, panelin camı, aynı saç teli.
          İki editör aynı bölümün iki kapısı; üst çubukları ayrışırsa bölüm iki
          ayrı yer gibi okunur. */}
      <div className="relative flex h-[68px] shrink-0 items-center gap-3 border-b border-white/10 bg-black/25 px-4 backdrop-blur-sm">
        <button
          onClick={() => navigate(sectionPath('points'))}
          className={editorIconButton()}
          title={T.strategicPoints.back}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span aria-hidden className="h-px w-4 shrink-0 bg-primary" />
        <span className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
          {T.strategy.tabs.points}
        </span>
        <span className="text-sm text-muted-foreground/30">/</span>
        {/* Harita adı veridir, büyütülmez. */}
        <span className="truncate font-display text-sm tracking-[0.04em] text-muted-foreground">{label}</span>
      </div>

      {/* ── Below topbar: sidebar + editor ───────────────────────── */}
      <div className="relative flex flex-1 overflow-hidden">

        {/* Mobil örtü — kenar çubuğu açıkken tuvalin üstüne iner, tıklanınca kapar */}
        {sidebarOpen && (
          <div
            className="absolute inset-0 z-10 bg-black/50 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sol kenar çubuğu — bu haritanın noktaları.
            Eskiden burada strateji listesi duruyordu: nokta katmanı ile maç
            planları ayrı bölümler, ve bu ekranın listesi ekranda duran şeyin
            listesi olmalı. md altında tuvalin üstünde katman, md+ akışta. */}
        <div className={`absolute inset-y-0 left-0 z-20 shrink-0 overflow-hidden transition-all duration-300 ease-in-out border-r border-white/10 md:static md:z-auto ${sidebarOpen ? 'w-72' : 'w-0'}`}>
          <div className="flex h-full w-72 flex-col bg-black/25 backdrop-blur-sm">
            <StrategicPointsSidebar
              points={points}
              selectedPointId={selectedPointId}
              onSelectPoint={handleSelectPoint}
              onDeletePoint={deletePoint}
              loading={loading}
              callouts={callouts}
              selectedCalloutId={selectedCalloutId}
              onSelectCallout={handleSelectCallout}
              onDeleteCallout={deleteCallout}
            />
          </div>
        </div>

        {/* Toggle tab */}
        <div className="flex items-center shrink-0">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="flex h-16 w-5 cursor-pointer items-center justify-center rounded-r-sm border-y border-r border-white/10 bg-black/40 text-muted-foreground backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-foreground"
            title={sidebarOpen ? T.strategy.sidebarClose : T.strategy.sidebarOpen}
          >
            <ChevronLeft className={`h-3.5 w-3.5 transition-transform duration-300 ${sidebarOpen ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Editor */}
        <StrategicPointEditor
          mapName={mapName ?? ''}
          points={points}
          selectedPointId={selectedPointId}
          onSelectPoint={handleSelectPoint}
          onAddPoint={handleAddPoint}
          onUpdatePoint={handleUpdatePoint}
          onRemovePoint={handleRemovePoint}
          tool={tool}
          onToolChange={setTool}
          callouts={callouts}
          selectedCalloutId={selectedCalloutId}
          onSelectCallout={handleSelectCallout}
          onAddCallout={handleAddCallout}
          onUpdateCallout={handleUpdateCallout}
          onDeleteCallout={deleteCallout}
        />
      </div>
    </div>
  )
}
